import { Wallet, AlertCircle, TrendingUp, CreditCard } from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// FIN-3 — 2 KPIs heroicos do crédito.
// Limite total (cyan, sempre estático) + Crédito usado (cor dinâmica
// + glow contextual via alert_level: ok / warning / critical).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

// Glow contextual via arbitrary values — não polui tokens do tema.
// Critical pulsa (animate-pulse padrão Tailwind), Warning fica sutil
// estático. Ambos respeitam prefers-reduced-motion via index.css.
const TONE = {
  ok:       { text: "text-brand-green",    bg: "bg-brand-green/10",    glow: "" },
  warning:  { text: "text-status-warning", bg: "bg-status-warning/10", glow: "shadow-[0_0_18px_rgba(245,158,11,0.35)]" },
  critical: { text: "text-status-error",   bg: "bg-status-error/10",   glow: "shadow-[0_0_24px_rgba(255,77,109,0.45)] animate-pulse" },
};

function Kpi({ icon: Icon, label, value, footer, color, bg, glow, testId }) {
  return (
    <Card
      variant="elevated"
      className={cn("glass-card glow-hover p-5 md:p-6 flex-1 min-w-0", glow)}
      data-testid={testId}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", bg)}>
          <Icon className={cn("w-4.5 h-4.5", color)} aria-hidden="true" />
        </div>
        <span
          className="text-caption text-foreground-muted uppercase font-semibold"
          style={{ letterSpacing: "0.10em" }}
        >
          {label}
        </span>
      </div>
      <div
        className={cn(
          "font-display font-bold leading-[0.95] tracking-[-0.02em] tabular-nums truncate mb-1.5",
          "text-[36px] md:text-[44px] lg:text-[52px]",
          color,
        )}
      >
        {value}
      </div>
      {footer && (
        <p className="text-caption text-foreground-muted">{footer}</p>
      )}
    </Card>
  );
}

export default function KpisCreditoRow({ creditUsage, creditCardsCount }) {
  const totalLimit = Number(creditUsage?.total_limit ?? 0);
  const totalUsed = Number(creditUsage?.total_used ?? 0);
  const usagePct = Number(creditUsage?.usage_pct ?? 0);
  const alert = creditUsage?.alert_level || "ok";
  const tone = TONE[alert] || TONE.ok;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5" data-testid="contas-kpis">
      <Kpi
        icon={Wallet}
        label="Limite total"
        value={fmtBRL(totalLimit)}
        footer={`${creditCardsCount} cartão${creditCardsCount === 1 ? "" : "ões"} ativo${creditCardsCount === 1 ? "" : "s"}`}
        color="text-brand-cyan"
        bg="bg-brand-cyan/10"
        glow=""
        testId="contas-kpi-limite"
      />
      <Kpi
        icon={alert === "critical" ? AlertCircle : (alert === "warning" ? TrendingUp : CreditCard)}
        label="Crédito usado"
        value={"−" + fmtBRL(totalUsed)}
        footer={`${usagePct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% do limite`}
        color={tone.text}
        bg={tone.bg}
        glow={tone.glow}
        testId="contas-kpi-credito-usado"
      />
    </div>
  );
}
